/**
 * Global event listeners that feed the navigation controller.
 *
 * Four installers, one per browser event source:
 *
 * - `installClickListener` - capture-phase click handler that
 *   intercepts navigable anchors before React's synthetic event
 *   system gets a chance to run. Honors the unsaved-changes
 *   guard and defers links marked with `data-sdx-confirm` to
 *   framework-specific modal flows.
 *
 * - `installHoverListener` - debounced mouseover handler that
 *   kicks off a prefetch after the configured intent delay.
 *   Prefetches in flight are NOT aborted on mouseout: if the
 *   user hovered long enough for the fetch to start, let it
 *   complete so the eventual click still hits the cache. A
 *   pending intent timer IS cancelled when any navigation
 *   starts - a fast click (or keyboard/programmatic activation)
 *   used to let the timer fire ~70ms after the real fetch and
 *   request the same page a second time.
 *
 * - `installFormListener` - bubble-phase submit handler that
 *   intercepts same-origin, non-multipart POST submissions when
 *   SDX engine is active. Hands the response's redirect URL to
 *   the navigation controller, or escalates into the batch modal
 *   when the redirect lands on `/batch?op=start`. Bubble phase
 *   is deliberate: framework forms (React `onSubmit` etc.) run
 *   their own handlers before the event reaches `document`, so
 *   `preventDefault()` there is honoured. Forms can also opt out
 *   per element with `data-sdx-navigate="false"` on the form or
 *   an ancestor, mirroring the link opt-out.
 *
 * - `installPopstateListener` - back/forward navigation. On the
 *   no-unsaved-changes fast path the SPA fetch is dispatched
 *   directly with no history mutation. On the slow path a trap
 *   entry is pushed for the duration of the confirm dialog and
 *   either committed (on confirm) or rewound via history.back()
 *   (on cancel).
 */

import { getLinkScope, isNavigable, isPrefetchable, resolveNavLink, toHistoryUrl } from './url';
import { confirmNavigation, hasUnsavedChanges } from './unsaved-guard';
import { BatchModal } from './batch-modal';
import { logger } from './logger';
import { readLoadedLibraries } from './fetch-page';
import { SDX_HEADERS, toFetchableUrl } from '@sdx/core/transport';
import { NAV_EVENT } from './types';
import { requiresDocumentNavigation } from './document-policy';
import type { NavigationController } from './navigation-controller';
import type { NavigableRules } from './url';
import type { ProgressBar } from './progress-bar';
import type { PrefetchCache } from './prefetch-cache';

export interface ListenerContext {
  readonly controller: NavigationController;
  readonly prefetchCache: PrefetchCache;
  readonly rules: NavigableRules;
  readonly prefetchDelay: number;
  readonly interceptForms: boolean;
  readonly batchModal: boolean;
  readonly progressBar: ProgressBar;
}

export function installClickListener(ctx: ListenerContext): void {
  document.addEventListener(
    'click',
    (event) => handleClick(event, ctx),
    true,
  );
}

export function installHoverListener(ctx: ListenerContext): void {
  let prefetchTimer: ReturnType<typeof setTimeout> | null = null;
  const cancelIntent = (): void => {
    if (prefetchTimer !== null) {
      clearTimeout(prefetchTimer);
      prefetchTimer = null;
    }
  };

  document.addEventListener('mouseover', (event) => {
    const link = resolveNavLink(event.target);
    if (!link || !isNavigable(link, ctx.rules)) return;
    if (!isPrefetchable(link)) return;

    // Sanitized here AND in the controller so the cache key stored
    // on hover matches the key the click-path lookup uses.
    const href = toFetchableUrl(link.href);
    if (ctx.prefetchCache.has(href)) return;
    if (link.element.getAttribute('data-sdx-prefetch') === 'false') return;

    cancelIntent();
    prefetchTimer = setTimeout(() => {
      prefetchTimer = null;
      // The intent delay may outlive a click on the same link: the
      // navigation is then already fetching this URL and a prefetch
      // would be a second, discarded server render.
      if (ctx.controller.isNavigatingTo(href)) return;
      // Abort any stale prefetch for the same URL. A user who
      // rapidly hovers in and out of the same link should get a
      // single clean request rather than a queue of them.
      ctx.prefetchCache.abort(href);
      ctx.prefetchCache.prefetch(href, getLinkScope(link));
    }, ctx.prefetchDelay);
  });

  document.addEventListener('mouseout', cancelIntent);

  // Any navigation start - click, keyboard activation, popstate or
  // the programmatic API - supersedes a pending hover intent: the
  // page it targeted is either the one now being fetched or one
  // whose link is about to be swapped out of the DOM.
  document.addEventListener(NAV_EVENT.START, cancelIntent);
}

export function installFormListener(ctx: ListenerContext): void {
  // Bubble phase, unlike the click listener: `handleSubmit` bails
  // on `event.defaultPrevented`, and the only phase in which an
  // application-level handler (a React `onSubmit`, a Drupal
  // behavior bound on the form) can have set that flag before we
  // look at it is after the event has passed the form itself.
  document.addEventListener('submit', (event) => handleSubmit(event, ctx));
}

export function installPopstateListener(ctx: ListenerContext): void {
  // Closure-scoped guards for the confirm-trap dance.
  //
  // `rewinding` swallows the synthetic popstate that fires when
  // history.back() rewinds a cancelled confirm trap, so the SPA
  // fetch is not dispatched against the URL the user just chose
  // to stay on.
  //
  // `confirmInFlight` drops further back presses while a confirm
  // dialog is open - stacking dialogs would be both ugly and
  // ambiguous about which decision targets which destination.
  //
  // We cannot encode either piece of state on history.state
  // because popstate reads the state of the entry being
  // navigated TO, not the one being left, and the entry we would
  // want to read from is the trap that history.back() removes.
  let rewinding = false;
  let confirmInFlight = false;

  // The document we are currently showing, as pathname + search.
  // Compared against each popstate to tell a route change from a
  // same-document fragment jump; updated on every navigation the
  // controller completes, whatever started it.
  let currentDoc = documentKey(location.href);
  document.addEventListener(NAV_EVENT.END, () => {
    currentDoc = documentKey(location.href);
  });

  window.addEventListener('popstate', () => {
    if (rewinding) {
      rewinding = false;
      return;
    }
    if (confirmInFlight) {
      return;
    }

    // Same-document fragment navigation, not a route change.
    //
    // Clicking an in-page anchor is already declined by
    // `isNavigable`, so the browser handles it natively and scrolls
    // to the target. Chrome then fires popstate for that same-
    // document navigation, and without this guard we would re-fetch
    // the page the user is already on and finish by scrolling it
    // back to the top - the anchor appears to work for a moment and
    // then snaps away. No link-level opt-out can prevent that:
    // popstate has no link to inspect.
    if (documentKey(location.href) === currentDoc) {
      return;
    }

    // Snapshot the URL the browser navigated TO before opening
    // the async confirm. The dialog may take seconds, and a
    // second back-button press would change location.href under
    // our feet were it not for the confirmInFlight guard above.
    // Sanitized before the trap pushState below so a credentialed
    // history entry is never written back and re-served on the
    // next popstate.
    const targetUrl = toFetchableUrl(location.href);

    // Fast path: nothing dirty, no prompt. The browser's native
    // popstate has already moved location to targetUrl, so the
    // SPA fetch is dispatched directly and no further history
    // manipulation happens. A pushState here would append a
    // duplicate entry at currentIndex+1 that the next Back press
    // consumes right back to, oscillating the user between two
    // entries carrying the same URL - the failure mode this
    // listener used to exhibit on every popstate.
    if (!hasUnsavedChanges()) {
      void ctx.controller.start(targetUrl, { pushState: false });
      return;
    }

    // Slow path: unsaved changes are present. Push a trap entry
    // so the address bar reflects the destination URL while the
    // confirm dialog awaits a decision. On confirm, the trap
    // becomes the committed history record for the new page. On
    // cancel, history.back() rewinds the trap and the synthetic
    // popstate is swallowed by the rewinding guard above.
    confirmInFlight = true;
    history.pushState({ sdxNav: true, sdxTrap: true }, '', toHistoryUrl(targetUrl));

    void confirmNavigation().then((ok) => {
      confirmInFlight = false;
      if (!ok) {
        rewinding = true;
        history.back();
        return;
      }
      void ctx.controller.start(targetUrl, { pushState: false });
    });
  });
}

/**
 * Identity of the document a URL addresses: everything except the
 * fragment. Two URLs sharing a key render the same page, so moving
 * between them is a scroll, not a navigation.
 *
 * Falls back to the raw string when the URL will not parse, which
 * makes an unparseable value compare unequal to a parsed one and so
 * errs toward navigating rather than silently swallowing a real
 * route change.
 */
function documentKey(url: string): string {
  try {
    const parsed = new URL(url, location.origin);
    return `${parsed.origin}${parsed.pathname}${parsed.search}`;
  }
  catch {
    return url;
  }
}

function handleClick(event: MouseEvent, ctx: ListenerContext): void {
  if (event.defaultPrevented) return;
  if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
  if (event.button !== 0) return;

  const link = resolveNavLink(event.target);
  if (!link || !isNavigable(link, ctx.rules)) return;

  // data-sdx-confirm is handled by framework-specific modal
  // flows defined by the consuming theme. We prevent the default
  // navigation so the browser does not follow the link, but we
  // do not start an SPA navigation either.
  if (link.element.getAttribute('data-sdx-confirm')) {
    event.preventDefault();
    return;
  }

  // Always preventDefault + stopImmediatePropagation up front so
  // neither the browser nor any downstream listener fires a
  // navigation before we've decided what to do. The unsaved-
  // changes guard is async (it awaits a confirm dialog), so the
  // browser cannot see a synchronous "should we navigate" answer;
  // we commit to handling the navigation ourselves and only
  // dispatch to the controller once the user confirms.
  event.preventDefault();
  event.stopImmediatePropagation();

  const href = link.href;
  const scope = getLinkScope(link);

  void confirmNavigation().then((ok) => {
    if (!ok) return;
    void ctx.controller.start(href, { scope });
  });
}

function handleSubmit(event: SubmitEvent, ctx: ListenerContext): void {
  if (!ctx.interceptForms) return;
  if (event.defaultPrevented) return;

  const form = event.target as HTMLFormElement | null;
  if (!form || form.tagName !== 'FORM') return;
  if (form.classList.contains('use-ajax')) return;
  if (form.closest('[data-sdx-navigate="false"]')) return;
  if (form.enctype === 'multipart/form-data') return;

  const actionAttr = form.getAttribute('action') ?? location.href;
  let action: URL;
  try {
    action = new URL(actionAttr, location.origin);
  }
  catch {
    return;
  }
  if (action.origin !== location.origin) return;
  if (requiresDocumentNavigation(action.href)) return;

  // Only intercept form submissions when the SDX engine is the
  // active page renderer. Non-SDX pages lack the state store the
  // navigation engine assumes and must stay on Drupal's native
  // form submission path.
  if (!document.querySelector('.sdx-component[data-component]')) return;

  event.preventDefault();

  const formData = new FormData(form);
  const submitter = event.submitter as HTMLButtonElement | HTMLInputElement | null;
  if (submitter?.name) {
    formData.set(submitter.name, submitter.value);
  }

  ctx.progressBar.start();
  const url = toFetchableUrl(action.toString());

  // Send the SPA-navigate marker so the server (`PartialResponseSubscriber`
  // for standard Drupal forms, `SpaNavigationSubscriber` for
  // DataProvider routes) wraps the post-submit response the same
  // way it would a fresh SPA fetch.
  //
  // `X-SDX-Ajax-Libraries` forwards the current document's
  // loaded-library manifest. Browsers preserve custom headers
  // across same-origin redirects, so the value survives the 303
  // Drupal form handlers emit after a successful POST - which a
  // query-parameter equivalent would not. Without this hygiene,
  // the adopted response re-delivers the full library bundle
  // (including `core/drupal`), whose evaluation resets
  // `window.Drupal = { behaviors: {} }` and wipes
  // `Drupal.sdxNavigation`; the first form submission surfaces
  // messages correctly but every subsequent SPA navigation fires
  // listeners whose closure points at a torn-down store and the
  // React shell freezes on the pre-submit content. `sdx_router`'s
  // `SdxAjaxLibrariesHeader` middleware lifts the header into
  // `$request->query['ajax_page_state']` so core's own
  // `AjaxPageState` middleware processes it uniformly.
  const headers: Record<string, string> = { [SDX_HEADERS.NAVIGATE]: '1' };
  const libraries = readLoadedLibraries();
  if (libraries !== null) {
    headers[SDX_HEADERS.AJAX_LIBRARIES] = libraries;
  }

  fetch(url, {
    method: form.getAttribute('method') ?? 'POST',
    body: formData,
    credentials: 'same-origin',
    redirect: 'follow',
    headers,
  })
    .then((response) => {
      const finalUrl = response.url || url;
      if (isBatchRedirect(finalUrl)) {
        ctx.progressBar.complete();
        if (ctx.batchModal) {
          new BatchModal().run(finalUrl, url, (dest) => {
            void ctx.controller.start(dest);
          });
        }
        else {
          window.location.href = finalUrl;
        }
        return;
      }
      ctx.progressBar.complete();
      // Hand the response body we already have to the controller
      // instead of re-fetching `finalUrl`. The previous re-fetch
      // discarded this body and consumed the Drupal messenger
      // flash on the POST render, so the follow-up GET rendered
      // an empty message bag and users saw no validation feedback.
      void ctx.controller.adoptResponse(response, finalUrl);
    })
    .catch((err: unknown) => {
      ctx.progressBar.complete();
      logger.warn('Form submission failed; falling back to native submit.', err);
      HTMLFormElement.prototype.submit.call(form);
    });
}

function isBatchRedirect(finalUrl: string): boolean {
  try {
    return new URL(finalUrl, location.origin).pathname === '/batch';
  }
  catch {
    return false;
  }
}
